// src/lib/naming.js
// ───────────────────────────────────────────────────────────────────────────
// 🏷️ Motor de Estandarización (PLAN-ARCHIVOS-PREPRESS §4)
// El sistema es la fuente de verdad de los nombres/códigos. "El desorden puede
// estar en las terminales, NO en el sistema." Genera el PAQUETE JSON que el
// script de Illustrator/terminal consume para aplicar el estándar (nombre + QR),
// generalizando el patrón de SheetModal (handleDownloadJson).
// ───────────────────────────────────────────────────────────────────────────
import { buildArtifactScanCode } from "./scanCode";

/** Convención de nombre estándar (documentada para humanos). */
export const STANDARD_NAME_CONVENTION =
  "{JOB}-{TIPO}_v{VV}.{ext}  ·  ej: J-1042-DIE_v02.pdf";

/**
 * Payload del QR de un artefacto (lo lee el escáner / la terminal). PURO.
 * Delegado al contrato único de scanCode.js (§9.3 Pilar 2) — se mantiene el export
 * (misma firma) por compatibilidad con los consumidores existentes
 * (FileArtifactsTab). Los QR viejos ya impresos los sigue aceptando parseScanInput().
 */
export function artifactQrPayload({ jobId, artifactId }) {
  return buildArtifactScanCode({ jobId, artifactId });
}

/**
 * Construye el paquete JSON para la terminal (estilo SheetModal). PURO.
 * El script de Illustrator usa esto para renombrar y colocar el QR según el estándar.
 * @param {object} job        { id, jobNumber, jobName }
 * @param {object} artifact   { id, type, currentVersion, latestName, latestOriginalName, approval }
 * @param {object} extra      { clientName, qrCodeBase64 }
 * @return {object}
 */
export function buildTerminalPackage(job, artifact, extra = {}) {
  return {
    standardName: artifact.latestName || "",
    originalName: artifact.latestOriginalName || "",
    jobId: job?.id || null,
    jobNumber: job?.jobNumber || null,
    jobName: job?.jobName || "",
    clientName: extra.clientName || "",
    artifactId: artifact.id || null,
    artifactType: artifact.type || null,
    version: artifact.currentVersion || null,
    approval: artifact.approval?.status || "pending",
    qrPayload: artifactQrPayload({ jobId: job?.id, artifactId: artifact.id, type: artifact.type, version: artifact.currentVersion }),
    qrCodeBase64: extra.qrCodeBase64 ? String(extra.qrCodeBase64).split(",").pop() : null,
    qrSizeInches: 0.75,
    generatedBy: "CARTONBAR · motor de estandarización",
  };
}

// ── Retales / paletas (PLAN-INVENTARIO-RETALES §5 y §4b) ────────────────────
// El CÓDIGO lo emite el servidor (createRemnant/counters); aquí solo se FORMATEA
// el estándar para nombres de archivo y rótulos — fuente única de la convención.

/** Código estándar de retal: RET-00042. PURO. */
export function formatRemnantCode(seq) {
  return `RET-${String(Number(seq) || 0).padStart(5, "0")}`;
}

/** Código estándar de paleta (license plate): PAL-007. PURO. */
export function formatPalletCode(seq) {
  return `PAL-${String(Number(seq) || 0).padStart(3, "0")}`;
}

// Medida limpia para nombres: hasta 4 decimales, sin ceros colgantes.
const dim = (v) => String(Math.round(Number(v) * 10000) / 10000);

/**
 * Nombre de archivo estándar del DXF de retal: RET-00042-12x18in.dxf.
 * Antes de guardar (aún sin código) cae a RET-12x18in.dxf. PURO.
 */
export function remnantDxfFilename({ code, widthIn, heightIn }) {
  const base = code ? String(code) : "RET";
  return `${base}-${dim(widthIn)}x${dim(heightIn)}in.dxf`;
}

/**
 * Manifiesto de estandarización del job: la lista de nombres estándar que debe
 * tener cada artefacto (para que la terminal no improvise). PURO.
 */
export function buildJobManifest(job, artifacts) {
  return {
    jobId: job?.id || null,
    jobName: job?.jobName || "",
    convention: STANDARD_NAME_CONVENTION,
    artifacts: (artifacts || []).map((a) => ({
      type: a.type,
      standardName: a.latestName || "",
      version: a.currentVersion || 0,
      approval: a.approval?.status || "pending",
    })),
  };
}
